import React, { createRef, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MsalProvider, useIsAuthenticated } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";


import { SnackbarProvider, SnackbarKey } from "notistack";
import Button from "@mui/material/Button";
import { DevTools } from "jotai-devtools";

import { CustomNavigationClient } from "./utils/NavigationClient";
import ProtectedLayout from "pages/ProtectedLayout";
import { AboutPage, AuthRedirectPage, LandingPage, PricingPage } from "pages/Landing";
import { TenantProvider } from "tenant/TenantContext";
import { AppProvider } from "context/AppContext";
import { QueryClient, QueryClientProvider } from "react-query";

type AppProps = {
  pca: PublicClientApplication;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
  },
});

//73483407-ce30-497e-8d2a-4b8bcb6310b2.43831993-6193-4999-9fd2-50e9fa682c56-login.windows.net-refreshtoken-43a1f395-3dcc-4f93-9b5f-64d16474421c----
const App: React.FC<AppProps> = ({ pca }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const navigationClient = new CustomNavigationClient(navigate);
  pca.setNavigationClient(navigationClient);

  const path = location.pathname.toLowerCase();

  let publicPage: ReactNode = null;
  switch (path) {
    case "/":
    case "/home":
    case "/landing":
      publicPage = <LandingPage />;
      break;
    case "/about":
      publicPage = <AboutPage />;
      break;
    case "/pricing":
      publicPage = <PricingPage />;
      break;
    case "/login":
      publicPage = <AuthRedirectPage mode="login" />;
      break;
    case "/register":
      publicPage = <AuthRedirectPage mode="register" />;
      break;
    default:
      publicPage = null;
  }

  const notistackRef = createRef<SnackbarProvider>();
  const onClickDismiss = (key: SnackbarKey) => () => {
    notistackRef.current?.closeSnackbar(key);
  };

  return (
    <AppProvider>
      <MsalProvider instance={pca}>
        <QueryClientProvider client={queryClient}>
          <TenantProvider>
            <DevTools />
            <SnackbarProvider
              preventDuplicate
              autoHideDuration={3000}
              maxSnack={5}
              ref={notistackRef}
              action={(key) => (
                <Button onClick={onClickDismiss(key)}>Dismiss</Button>
              )}
            >
              {!isAuthenticated && publicPage ? publicPage : <ProtectedLayout />}
            </SnackbarProvider>

            {/* <ReactQueryDevtools initialIsOpen={false} position="bottom-right" /> */}
          </TenantProvider>
        </QueryClientProvider>
      </MsalProvider>
    </AppProvider>
  );
};

export default App;
